import { DateTime } from 'luxon'

/**
 * A reading older than this counts as "stale" for the admin tables' visible
 * marker (task 6.2 / review finding — see `describeLastReading` below). Most
 * meters report daily; two days gives a day of slack before flagging a feed
 * as stalled, without needing per-provider fetch-cadence data client-side.
 */
export const STALE_AFTER_HOURS = 48

/**
 * Single source of the "how recent is this reading" copy, shared by
 * FreshnessLine (user-facing pages) and the admin location/meter tables'
 * Last Reading column (task 6.2 — "format it consistently with the
 * freshness copy used elsewhere"), so the two surfaces never drift apart.
 *
 * Computed from the raw timestamp at call time, never from a
 * server-formatted string, so a value read long after the API responded
 * still reports an accurate age (design.md "Relative dates drift without a
 * re-render").
 *
 * `isStale` marks a row for the admin tables' visible highlight (spec:
 * "Stalled meter is visible in the list"). A never-received row is also
 * stale — there's nothing staler than a feed that has never reported.
 */
export function describeLastReading(lastReadingAt) {
    if (!lastReadingAt) {
        return {
            text: "Data hasn't started arriving yet",
            neverReceived: true,
            isStale: true
        }
    }

    const readingTime = DateTime.fromISO(lastReadingAt)
    if (!readingTime.isValid) {
        return {
            text: 'Last reading time unavailable',
            neverReceived: false,
            isStale: false
        }
    }

    return {
        text: `Last reading ${readingTime.toRelative()}`,
        neverReceived: false,
        isStale:
            DateTime.now().diff(readingTime, 'hours').hours > STALE_AFTER_HOURS,
        dateTime: readingTime
    }
}
